import { Alert } from '@mui/material';
import { useMemo, useState } from 'react';
import ReactPaginate from 'react-paginate';
import styles from 'src/pages/accueil/styles/style-setting.module.scss';
import { formatNumber } from 'src/utils/formatNumber';
import ChargesColumns from './charges-columns';

type ProductionCycleChargesTableProps = {
  t: (key: string) => string;
  charges: any[];
  showEditAction?: boolean;
  onEditCharge?: (charge: any, index: number) => void;
  pageSize?: number;
  emptyMessageKey?: string;
};

const ProductionCycleChargesTable = ({
  t,
  charges = [],
  showEditAction = false,
  onEditCharge,
  pageSize = 10,
  emptyMessageKey = 'production_cycle.no_charges',
}: ProductionCycleChargesTableProps) => {
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(0);

  const filteredCharges = useMemo(() => {
    const needle = search.trim().toUpperCase();
    if (!needle) return charges;

    return charges.filter((charge: any) => {
      const nature = charge?.natureCharge?.name || '';
      const description = charge?.description || '';
      const currency = charge?.currency || 'MAD';
      const amount = charge?.amount != null ? String(charge.amount) : '0';

      return [nature, description, currency, amount].some((value) => value.toUpperCase().includes(needle));
    });
  }, [charges, search]);

  const pagesVisited = pageNumber * pageSize;
  const pageCount = Math.ceil(filteredCharges.length / pageSize);

  const rows = filteredCharges.slice(pagesVisited, pagesVisited + pageSize).map((charge: any, index: number) => ({
    nature: charge?.natureCharge?.name || '-',
    description: charge?.description || '-',
    amountValue: Number(charge?.amount || 0),
    amount: formatNumber(charge?.amount ?? 0),
    currency: charge?.currency || 'MAD',
    sourceIndex: pagesVisited + index,
    originalCharge: charge,
  }));

  return (
    <>
      <input
        className="form-control mb-2"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPageNumber(0);
        }}
        placeholder={t('search_unit')}
      />

      {rows.length === 0 ? (
        <Alert variant="outlined" severity="info">
          {t(emptyMessageKey)}
        </Alert>
      ) : (
        <ChargesColumns t={t} data={rows} showEditAction={showEditAction} onEditCharge={onEditCharge} />
      )}

      {filteredCharges.length > pageSize && (
        <div className="d-flex justify-content-end mt-3">
          <ReactPaginate
            previousLabel="<"
            nextLabel=">"
            pageCount={pageCount}
            onPageChange={({ selected }) => setPageNumber(selected)}
            forcePage={pageNumber}
            containerClassName={styles['paginationButton']}
            previousLinkClassName={styles['previousBtn']}
            nextLinkClassName={styles['nextBtn']}
            disabledClassName={styles['paginationDisabled']}
            activeClassName={styles['paginationActive']}
          />
        </div>
      )}
    </>
  );
};

export default ProductionCycleChargesTable;
